/** Observa a rolagem e reporta a posição dentro da página (N16, `scroll_pct`).
 *
 * Antes disto o `scroll_pct` era sempre 0: a retomada voltava à página certa,
 * mas ao topo dela — e numa página de livro didático ampliada isso é meia tela
 * de diferença.
 *
 * A quantização mora aqui, e não no `useProgress`: aquele hook compara a
 * posição com `toFixed(2)` para decidir se houve mudança, e um valor contínuo
 * faria toda rolagem gerar heartbeat.
 */
import { useEffect } from 'react'

import { pctDaRolagem, quantizar } from '@/features/reader/utils/scroll'

interface Opcoes {
  alvo: HTMLElement | null
  onMudar: (pct: number) => void
  habilitado?: boolean
}

export function useScrollPosition({ alvo, onMudar, habilitado = true }: Opcoes): void {
  useEffect(() => {
    if (!alvo || !habilitado) return

    let quadro = 0
    let ultimo = -1

    const medir = () => {
      quadro = 0
      const pct = quantizar(pctDaRolagem(alvo.scrollTop, alvo.scrollHeight, alvo.clientHeight))
      // Só reporta quando o PASSO mudou: `onMudar` acorda o store, e acordar o
      // store a cada quadro de rolagem re-renderiza o leitor inteiro.
      if (pct === ultimo) return
      ultimo = pct
      onMudar(pct)
    }

    // `requestAnimationFrame` e não *debounce* por timer: a rolagem já é
    // sincronizada com o quadro, e um timer atrasaria a última medição — que é
    // justamente a que o aluno deixa ao parar de rolar.
    const aoRolar = () => {
      if (quadro !== 0) return
      quadro = requestAnimationFrame(medir)
    }

    medir()
    alvo.addEventListener('scroll', aoRolar, { passive: true })
    return () => {
      alvo.removeEventListener('scroll', aoRolar)
      if (quadro !== 0) cancelAnimationFrame(quadro)
    }
  }, [alvo, onMudar, habilitado])
}
